import React, { useMemo, useState } from "react";
import { Bell, ChevronDown, ChevronUp, User } from "lucide-react";
import { formatBRL, dueState } from "./LoanDetailsDialog";
import LoanRow from "./LoanRow";

export default function LoansByCustomer({ loans, customerById, today, onOpenLoan, statusLabel }) {
  const [openIds, setOpenIds] = useState({});

  const groups = useMemo(() => {
    const map = new Map();
    for (const l of loans) {
      const g = map.get(l.customer_id) || { customerId: l.customer_id, loans: [], total: 0, remaining: 0, late: 0, today: 0 };
      g.loans.push(l);
      g.total += l.total_receivable || 0;
      g.remaining += Math.max(0, l.total_receivable - l.total_paid);
      const d = dueState(l, today);
      if (d === "late") g.late += 1;
      if (d === "today") g.today += 1;
      map.set(l.customer_id, g);
    }
    const rank = (g) => (g.late ? 0 : g.today ? 1 : 2);
    return [...map.values()]
      .map((g) => ({ ...g, name: customerById.get(g.customerId)?.name || "Cliente removido" }))
      .sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name, "pt-BR"));
  }, [loans, customerById, today]);

  const toggle = (id) => setOpenIds((o) => ({ ...o, [id]: !o[id] }));

  return (
    <div className="space-y-2">
      {groups.map((g) => {
        const open = !!openIds[g.customerId];
        return (
          <div
            key={g.customerId}
            data-testid={`customer-group-${g.customerId}`}
            className="overflow-hidden rounded-xl border border-white/10 bg-[#121212]"
          >
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#D4FF00]/30 bg-[#D4FF00]/10 text-[#D4FF00]">
                <User className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate font-display text-base font-bold text-white">{g.name}</span>
                  {g.late > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-sm bg-red-500/20 px-1.5 py-0.5 text-[0.62rem] font-bold uppercase tracking-wider text-red-300">
                      <Bell className="h-2.5 w-2.5" /> {g.late} atrasad{g.late > 1 ? "os" : "o"}
                    </span>
                  )}
                  {g.today > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-sm bg-[#D4FF00]/20 px-1.5 py-0.5 text-[0.62rem] font-bold uppercase tracking-wider text-[#D4FF00]">
                      <Bell className="h-2.5 w-2.5" /> vence hoje
                    </span>
                  )}
                </div>
                <div className="mt-0.5 text-[0.75rem] text-zinc-500">
                  {g.loans.length} empréstimo{g.loans.length > 1 ? "s" : ""} {statusLabel}
                </div>
              </div>
              <div className="hidden shrink-0 text-right sm:block">
                <div className="font-mono-num text-sm font-bold text-[#D4FF00]">{formatBRL(g.total)}</div>
                <div className="text-[0.7rem] text-zinc-500">
                  Restante{" "}
                  <span className="font-mono-num font-semibold" style={{ color: g.remaining > 0 ? "#F59E0B" : "#00FF66" }}>
                    {formatBRL(g.remaining)}
                  </span>
                </div>
              </div>
              <button
                data-testid={`customer-group-toggle-${g.customerId}`}
                onClick={() => toggle(g.customerId)}
                className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold ${
                  open ? "border-[#D4FF00] bg-[#D4FF00] text-black" : "border-white/15 text-zinc-200 hover:bg-white/5"
                }`}
                style={{ transition: "background-color 0.2s ease, color 0.2s ease" }}
              >
                {open ? "Ocultar" : "Ver mais"}
                {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              </button>
            </div>
            <div className="flex items-center justify-between border-t border-white/5 px-4 py-2 text-[0.7rem] text-zinc-500 sm:hidden">
              <span>
                Total <span className="font-mono-num font-semibold text-[#D4FF00]">{formatBRL(g.total)}</span>
              </span>
              <span>
                Restante <span className="font-mono-num font-semibold text-[#F59E0B]">{formatBRL(g.remaining)}</span>
              </span>
            </div>
            {open && (
              <div data-testid={`customer-group-loans-${g.customerId}`} className="border-t border-white/10 bg-black/20">
                {g.loans.map((l, i) => (
                  <LoanRow key={l.id} loan={l} today={today} showCustomer={false} border={i > 0} compact onClick={() => onOpenLoan(l)} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
